import { Link } from 'react-router-dom'
import { ShieldCheck, BarChart2, Users, FileText, ArrowRight, MapPin } from 'lucide-react'
import { hasCompletedQuestionnaire } from '../engine/matchEngine'

const features = [
  {
    icon: <BarChart2 className="h-6 w-6 text-civitas-gold" />,
    title: 'Science-Based Matching',
    desc: 'Our weighted similarity algorithm computes your alignment across 14 policy categories — no spin, no ideology.',
  },
  {
    icon: <ShieldCheck className="h-6 w-6 text-civitas-gold" />,
    title: 'Privacy by Design',
    desc: 'Your answers never leave your device. We use local storage only — the server sees nothing about your political views.',
  },
  {
    icon: <Users className="h-6 w-6 text-civitas-gold" />,
    title: 'Verified Evidence',
    desc: 'Every candidate stance is backed by an Evidence Ledger — actual votes, bills, and public statements with source links.',
  },
  {
    icon: <FileText className="h-6 w-6 text-civitas-gold" />,
    title: 'Follow the Money',
    desc: 'See who funds each campaign and connect the dots between donors, industries, and legislative votes.',
  },
]

const steps = [
  { num: '01', title: 'Rate Your Priorities', desc: 'Weigh 14 policy categories by personal importance (1–10).' },
  { num: '02', title: 'Share Your Stances', desc: 'Answer 12 policy questions on a -5 to +5 scale.' },
  { num: '03', title: 'See Your Matches', desc: 'Instantly see ranked candidate alignment scores.' },
  { num: '04', title: 'Dig Deeper', desc: 'Explore evidence ledgers, campaign finance, and side-by-side comparisons.' },
]

export default function Home() {
  const completed = hasCompletedQuestionnaire()

  return (
    <div>
      {/* Hero */}
      <section className="bg-civitas-navy text-white py-20 px-4">
        <div className="mx-auto max-w-4xl text-center">
          <span className="badge bg-civitas-gold/20 text-civitas-gold mb-4 inline-block">
            2026 General Election — All 50 States
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
            Know who you&apos;re really voting for.
          </h1>
          <p className="text-lg text-civitas-silver max-w-2xl mx-auto mb-8">
            BECivitas matches you to candidates based on verified policy positions — not ads, not headlines.
            Your data stays on your device. Always.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/questionnaire" className="btn-primary text-base px-8 py-3 flex items-center justify-center gap-2">
              {completed ? 'Retake Questionnaire' : 'Find Your Match'}
              <ArrowRight className="h-4 w-4" />
            </Link>
            {completed && (
              <Link to="/results" className="btn-secondary text-base px-8 py-3 border-white text-white hover:bg-white/10">
                View My Results
              </Link>
            )}
            <Link to="/how-it-works" className="btn-secondary text-base px-8 py-3 border-white/30 text-civitas-silver hover:bg-white/10">
              How It Works
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl text-civitas-navy mb-3">Built for informed voters</h2>
          <p className="text-civitas-silver max-w-xl mx-auto">
            Civic tech that puts accuracy and privacy first — designed for the November 2026 General Election.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map(f => (
            <div key={f.title} className="card flex flex-col gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-civitas-navy">
                {f.icon}
              </div>
              <h3 className="font-semibold text-civitas-navy">{f.title}</h3>
              <p className="text-sm text-civitas-silver">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-civitas-light py-16 px-4">
        <div className="mx-auto max-w-4xl">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl text-civitas-navy mb-3">How it works</h2>
            <p className="text-civitas-silver">Four steps to know where every candidate stands on what matters to you.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map(s => (
              <div key={s.num} className="flex flex-col gap-2">
                <span className="font-display text-4xl font-bold text-civitas-gold">{s.num}</span>
                <h4 className="font-semibold text-civitas-navy">{s.title}</h4>
                <p className="text-sm text-civitas-silver">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/questionnaire" className="btn-primary px-8 py-3 text-base">
              Start the Questionnaire
            </Link>
          </div>
        </div>
      </section>

      {/* Coverage stats */}
      <section className="section">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl text-civitas-navy mb-3">2026 Election Coverage</h2>
          <p className="text-civitas-silver max-w-xl mx-auto">
            Tracking federal and state candidates across every state for the November 2026 General Election.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto mb-10">
          <div className="card text-center">
            <p className="font-display text-5xl font-bold text-civitas-gold mb-2">4,400+</p>
            <p className="text-civitas-navy font-semibold">Candidates Tracked</p>
            <p className="text-sm text-civitas-silver mt-1">U.S. House, Senate &amp; State races</p>
          </div>
          <div className="card text-center">
            <p className="font-display text-5xl font-bold text-civitas-gold mb-2">50</p>
            <p className="text-civitas-navy font-semibold">States Covered</p>
            <p className="text-sm text-civitas-silver mt-1">Every state, every major race</p>
          </div>
          <div className="card text-center">
            <p className="font-display text-5xl font-bold text-civitas-gold mb-2">14</p>
            <p className="text-civitas-navy font-semibold">Policy Dimensions</p>
            <p className="text-sm text-civitas-silver mt-1">Scored from verified voting records</p>
          </div>
        </div>
        <div className="text-center">
          <Link to="/candidates" className="btn-secondary inline-flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Browse Candidates in Your State
          </Link>
        </div>
      </section>

      {/* Privacy callout */}
      <section className="bg-civitas-navy text-white py-12 px-4">
        <div className="mx-auto max-w-3xl text-center">
          <ShieldCheck className="h-12 w-12 text-civitas-gold mx-auto mb-4" />
          <h2 className="font-display text-2xl font-bold mb-3">Your political data is yours alone.</h2>
          <p className="text-civitas-silver mb-6">
            BECivitas is built on Privacy-by-Design principles. Your questionnaire responses are stored
            only in your browser&apos;s local storage. We never collect, transmit, or sell your political preferences.
          </p>
          <Link to="/privacy" className="text-civitas-gold underline text-sm hover:text-white">
            Read our Privacy Policy →
          </Link>
        </div>
      </section>
    </div>
  )
}
